import { useState, useRef, useEffect, useCallback } from "react";
import { Mic, Square, Play, Pause, Trash2, Plus, AlertTriangle } from "lucide-react";
import { Button } from "../../../components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_DURATION = 180; // 3 minutes
const MAX_RECORDINGS = 5;
const SILENCE_THRESHOLD_DB = -60;

function formatDuration(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function RecordingItem({ rec, token, onDelete }) {
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  const handlePlay = () => {
    if (!audioRef.current) {
      audioRef.current = new Audio(`${API}/api/assessment/voice-audio/${rec.audio_url || ""}`);
      audioRef.current.onended = () => setPlaying(false);
      audioRef.current.onerror = () => setPlaying(false);
      // WebM duration:Infinity workaround — use known duration to stop playback
      if (rec.duration_seconds) {
        audioRef.current.ontimeupdate = () => {
          if (audioRef.current && audioRef.current.currentTime >= rec.duration_seconds + 1) {
            audioRef.current.pause();
            setPlaying(false);
          }
        };
      }
    }
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => setPlaying(false));
      setPlaying(true);
    }
  };

  useEffect(() => {
    return () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } };
  }, []);

  return (
    <div
      className="flex items-center gap-2 bg-[#1a1a2e] rounded-lg px-3 py-2 border border-[#2a2a3e]"
      data-testid={`voice-recording-item-${rec.recording_number}`}
    >
      <Mic className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
      <span className="text-xs text-gray-300">
        Recording {rec.recording_number}
      </span>
      <span className="text-xs text-gray-500">({formatDuration(rec.duration_seconds)})</span>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-6 w-6 p-0 text-gray-400 hover:text-[#00AEEF]"
        onClick={handlePlay}
        data-testid={`voice-play-${rec.recording_number}`}
      >
        {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-6 w-6 p-0 text-gray-400 hover:text-red-400"
        onClick={() => onDelete(rec.id)}
        data-testid={`voice-delete-${rec.recording_number}`}
      >
        <Trash2 className="w-3.5 h-3.5" />
      </Button>
    </div>
  );
}

export function VoiceRecorder({ token, questionId, sectionId, onTranscription }) {
  const [recordings, setRecordings] = useState([]);
  const [state, setState] = useState("idle"); // idle, recording, uploading, silent_warning
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [pendingBlob, setPendingBlob] = useState(null);
  const [pendingVolume, setPendingVolume] = useState(null);

  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);
  const elapsedRef = useRef(0);

  // Load existing recordings on mount
  useEffect(() => {
    if (!token || !questionId || loaded) return;
    fetch(`${API}/api/assessment/${token}/voice/${questionId}`)
      .then(r => r.json())
      .then(data => {
        if (data.recordings) setRecordings(data.recordings);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, [token, questionId, loaded]);

  const cleanup = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    mediaRef.current = null;
    streamRef.current = null;
    chunksRef.current = [];
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const startRecording = async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Validate audio tracks are active
      const tracks = stream.getAudioTracks();
      console.log('[VoiceRecorder] Audio tracks:', tracks.length, tracks[0]?.label, 'enabled:', tracks[0]?.enabled, 'muted:', tracks[0]?.muted);
      if (tracks.length === 0 || !tracks[0].enabled) {
        setError("No active microphone detected. Please check your microphone settings.");
        stream.getTracks().forEach(t => t.stop());
        return;
      }

      const recorder = new MediaRecorder(stream, { mimeType: "audio/webm;codecs=opus" });
      mediaRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        console.log('[VoiceRecorder] Chunk received, size:', e.data.size, 'bytes');
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => handleRecordingComplete();

      recorder.start(1000);
      setState("recording");
      setElapsed(0);
      elapsedRef.current = 0;
      window.dispatchEvent(new CustomEvent("voice-activity", { detail: { action: "started", questionId, sectionId } }));
      timerRef.current = setInterval(() => {
        elapsedRef.current += 1;
        setElapsed(prev => {
          const next = prev + 1;
          if (next >= MAX_DURATION) {
            // Auto-save at 3 min — clear timer first, then stop recorder
            if (timerRef.current) clearInterval(timerRef.current);
            if (mediaRef.current && mediaRef.current.state === "recording") {
              mediaRef.current.stop();
            }
            return MAX_DURATION;
          }
          return next;
        });
      }, 1000);
    } catch {
      setError("Microphone access denied. Please allow microphone permissions.");
    }
  };

  const stopRecording = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (mediaRef.current && mediaRef.current.state === "recording") {
      mediaRef.current.stop();
    }
  };

  const handleRecordingComplete = async () => {
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    const blob = new Blob(chunksRef.current, { type: "audio/webm" });
    const duration = elapsedRef.current || 1;

    if (duration < 5) {
      setError("Recording too short (minimum 5 seconds)");
      setState("idle");
      return;
    }

    // Volume check using Web Audio API
    let volumeDb = -100;
    try {
      const ctx = new AudioContext();
      const buf = await ctx.decodeAudioData(await blob.arrayBuffer());
      const data = buf.getChannelData(0);
      let sum = 0;
      for (let i = 0; i < data.length; i++) sum += data[i] * data[i];
      const rms = Math.sqrt(sum / data.length);
      volumeDb = 20 * Math.log10(rms || 1e-10);
      ctx.close();
    } catch {
      // If volume check fails, proceed without warning
      volumeDb = 0;
    }

    if (volumeDb <= SILENCE_THRESHOLD_DB) {
      // Show silent recording warning
      setPendingBlob(blob);
      setPendingVolume(volumeDb);
      setState("silent_warning");
      return;
    }

    // Volume OK — upload directly
    await uploadRecording(blob, duration, false, false, volumeDb);
    window.dispatchEvent(new CustomEvent("voice-activity", { detail: { action: "completed", questionId, sectionId, duration_ms: duration * 1000 } }));
  };

  const handleSilentAction = async (action) => {
    const duration = elapsedRef.current || 1;
    // Log the warning event
    fetch(`${API}/api/assessment/${token}/silent-warning-log`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        question_id: questionId,
        section_id: sectionId,
        recording_duration_seconds: duration,
        mean_volume_db: Math.round(pendingVolume * 10) / 10,
        candidate_action: action === "save" ? "saved_anyway" : "discarded",
      }),
    }).catch(() => {});

    if (action === "save") {
      await uploadRecording(pendingBlob, duration, true, true, pendingVolume);
    } else {
      // Discard — reset to idle so candidate can re-record
      setState("idle");
      setPendingBlob(null);
      setPendingVolume(null);
    }
  };

  const uploadRecording = async (blob, duration, silentWarningShown, savedAfterWarning, volumeDb) => {
    setState("uploading");
    setPendingBlob(null);
    setPendingVolume(null);

    const fd = new FormData();
    fd.append("audio_file", blob, "recording.webm");
    fd.append("question_id", questionId);
    fd.append("section_id", sectionId);
    fd.append("duration_seconds", String(Math.round(duration)));
    fd.append("silent_warning_shown", String(silentWarningShown));
    fd.append("saved_after_warning", String(savedAfterWarning));
    if (volumeDb !== null && volumeDb !== undefined) {
      fd.append("mean_volume_db", String(Math.round(volumeDb * 10) / 10));
    }

    try {
      const resp = await fetch(`${API}/api/assessment/${token}/voice-upload`, {
        method: "POST", body: fd,
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || "Upload failed");
      }
      const data = await resp.json();
      setRecordings(prev => [...prev, {
        id: data.recording_id,
        recording_number: data.recording_number,
        duration_seconds: data.duration_seconds,
        audio_url: data.audio_url,
      }]);
      setState("idle");
    } catch (e) {
      setError(e.message || "Upload failed");
      setState("idle");
    }
  };

  const handleDelete = async (recordingId) => {
    try {
      const resp = await fetch(`${API}/api/assessment/${token}/voice/${recordingId}`, {
        method: "DELETE",
      });
      if (resp.ok) {
        setRecordings(prev => prev.filter(r => r.id !== recordingId));
      }
    } catch { /* silent */ }
  };

  const canRecord = recordings.length < MAX_RECORDINGS && state === "idle";

  return (
    <div className="space-y-2" data-testid={`voice-recorder-${questionId}`}>
      {/* Existing recordings */}
      {recordings.map(rec => (
        <RecordingItem
          key={rec.id}
          rec={rec}
          token={token}
          onDelete={handleDelete}
        />
      ))}

      {/* Recording in progress — dark background matching completed recording style */}
      {state === "recording" && (
        <div className="flex items-center gap-3 bg-[#1a1a2e] border border-[#2a2a3e] rounded-lg px-3 py-2">
          <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
          <span className="text-xs text-gray-200 font-medium">Recording... {formatDuration(elapsed)}</span>
          <span className="text-[10px] text-gray-400">(max {MAX_DURATION / 60}m)</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-gray-200 hover:text-white hover:bg-white/10"
            onClick={stopRecording}
            data-testid="voice-stop"
          >
            <Square className="w-3 h-3 mr-1" /> Stop
          </Button>
        </div>
      )}

      {/* Silent recording warning */}
      {state === "silent_warning" && (
        <div className="bg-amber-950/30 border border-amber-700/40 rounded-lg px-4 py-3 space-y-3" data-testid="voice-silent-warning">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
            <div>
              <p className="text-xs font-medium text-amber-300">Your recording appears to be silent.</p>
              <p className="text-[11px] text-amber-400/70 mt-1">
                Your microphone may not be working. Please check your microphone settings and try again.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              type="button" size="sm"
              onClick={() => handleSilentAction("discard")}
              className="h-7 px-3 text-xs bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="voice-silent-rerecord"
            >
              Re-record
            </Button>
            <Button
              type="button" variant="ghost" size="sm"
              onClick={() => handleSilentAction("save")}
              className="h-7 px-3 text-xs text-amber-400 hover:text-amber-300"
              data-testid="voice-silent-save"
            >
              Save Anyway
            </Button>
          </div>
        </div>
      )}

      {/* Uploading */}
      {state === "uploading" && (
        <div className="flex items-center gap-2 text-xs text-gray-400 px-3 py-2">
          <div className="w-3 h-3 border-2 border-[#00AEEF] border-t-transparent rounded-full animate-spin" />
          Saving voice response...
        </div>
      )}

      {/* Record button */}
      {state === "idle" && (
        <div className="flex items-center gap-2">
          {canRecord && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={startRecording}
              className="h-7 px-3 text-xs text-gray-400 hover:text-[#00AEEF] border border-[#2a2a3e] hover:border-[#00AEEF]/40"
              data-testid={recordings.length > 0 ? "voice-record-another" : "voice-start"}
            >
              {recordings.length > 0 ? (
                <><Plus className="w-3 h-3 mr-1.5" /> Record Another</>
              ) : (
                <><Mic className="w-3 h-3 mr-1.5" /> Record Voice Response</>
              )}
            </Button>
          )}
          {recordings.length >= MAX_RECORDINGS && (
            <span className="text-[10px] text-gray-500">Maximum {MAX_RECORDINGS} recordings reached</span>
          )}
        </div>
      )}

      {/* Error */}
      {error && (
        <p className="text-xs text-red-400 px-1" data-testid="voice-error">{error}</p>
      )}
    </div>
  );
}
